import { Button } from '@mui/material';

import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { filterFormSchema } from './schema';
import FiltersGroup, { NumberFilterType, StringFilterType } from './FiltersGroup';

export type FiltersFormValues = InferType<typeof filterFormSchema>;
export type FilterFieldType = 'string' | 'number' | 'date' | 'select';
export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  options?: string[];
  nullable: boolean;
  defaultMode?: StringFilterType | NumberFilterType;
  range?: boolean;
}

export const filterIconByType: Record<FilterFieldType, string> = {
  string: 'material-outline:title',
  number: 'material-outline:tag',
  date: 'material-outline:calendar_today',
  select: 'material-outline:title',
};

interface IProps {
  fields: FilterField[];
  selectedFilters?: FiltersFormValues;
  onFiltersClear: () => void;
  onFiltersApply: (v: FiltersFormValues) => void;
}

const FiltersForm = ({
  fields: options,
  onFiltersApply,
  onFiltersClear,
  selectedFilters,
}: IProps) => {
  const methods = useForm<FiltersFormValues>({
    resolver: yupResolver(filterFormSchema),
    mode: 'onChange',
    values: selectedFilters
      ? { ...selectedFilters }
      : {
          filters: [
            {
              isGroup: false,
              mode: 'AND',
              rules: [
                {
                  field: options[0].key,
                  mode: options[0].defaultMode ?? '',
                  value: '',
                  type: options[0].type,
                },
              ],
            },
          ],
        },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'filters',
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit((d) => {
          console.log(d);

          onFiltersApply(d);
        })}
      >
        {fields.map((field, index) => (
          <FiltersGroup
            key={field.id}
            field={field as any}
            index={index}
            options={options}
            removeRule={() => remove(index)}
            removable={fields.length > 1}
          />
        ))}
        <div className="flex justify-between mb-20 p-0">
          <Button
            size="small"
            variant="contained"
            className=" font-400 "
            onClick={() => {
              append({
                isGroup: false,
                mode: 'AND',
                rules: [
                  {
                    field: options[0].key,
                    mode: options[0].defaultMode ?? '',
                    value: '',
                    type: options[0].type,
                  },
                ],
              });
            }}
          >
            <FuseSvgIcon className="mr-4">material-outline:add</FuseSvgIcon> Add
          </Button>

          <div className="flex gap-12  ">
            <Button
              onClick={() => {
                reset();
                onFiltersClear();
              }}
              className="flex items-center text-gray-600 font-500 bg-gray-50 hover:bg-gray-200 px-16 py-8"
            >
              Clear all
            </Button>
            <Button
              disabled={!isValid}
              type="submit"
              className="text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8 disabled:bg-gray-400 disabled:text-opacity-60"
            >
              Apply filters
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default FiltersForm;
